import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { z } from 'zod'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { PageLoadingSpinner } from 'ui_kit_qmd'
import { clearSessionLocally, logout } from 'store/auth/AuthActions'
import { LOGIN_SUCCESS } from 'store/home/HomeConstants'
import { getProviderProfile } from 'store/waitingRoom/WaitingRoomThunk'
import { getFlags } from 'store/flags/FlagsActions'
import { WAITING_ROOM_PAGE_PATH } from 'utils/constants'
import { decodeRolesFromToken, getDefaultHomePath } from 'auth/roles'
import env from 'env.json'

const FETCH_TIMEOUT_MS = 10_000

const RefreshTokenResponse = z.object({ AccessToken: z.string() })

function parseParamsFromSearch(searchParams: URLSearchParams) {
  const accessToken = searchParams.get('accessToken') ?? ''
  const redirectTo = searchParams.get('redirectTo') ?? ''

  const safeRedirectTo = /^(https?:|javascript:|vbscript:|data:|\/\/)/.test(redirectTo) ? '' : redirectTo

  const forwardParams = new URLSearchParams()
  Array.from(searchParams.entries()).forEach(([key, value]) => {
    if (key !== 'accessToken' && key !== 'redirectTo') {
      forwardParams.set(key, value)
    }
  })
  const queryString = forwardParams.toString()
  const cleanPath = safeRedirectTo.replace(/^\//, '')
  const fullLocation = queryString ? `${cleanPath}?${queryString}` : cleanPath

  return { accessToken, location: fullLocation }
}

export function AuthValidate() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const parsedRef = useRef(parseParamsFromSearch(searchParams))

  useEffect(() => {
    const { accessToken, location } = parsedRef.current

    if (!accessToken) {
      navigate(WAITING_ROOM_PAGE_PATH, { replace: true })
      return
    }

    // Local-only: this tab must stop showing the outgoing provider's data immediately, but
    // the replacement token has not been validated yet. Broadcasting this clear would let a
    // stale or expired link — or a 10s fetch timeout — log out a sibling tab that is mid
    // video visit. The cross-tab half is committed below, paired with `LOGIN_SUCCESS`.
    dispatch(clearSessionLocally())

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    async function authenticate() {
      try {
        getFlags()

        const response = await fetch(`${env.baseURL}api/v1/auth/refreshtoken`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ AccessToken: accessToken }),
          signal: controller.signal,
        })

        if (!response.ok) {
          navigate(WAITING_ROOM_PAGE_PATH, { replace: true })
          return
        }

        const result = RefreshTokenResponse.safeParse(await response.json())

        if (!result.success) {
          navigate(WAITING_ROOM_PAGE_PATH, { replace: true })
          return
        }

        // Only now is the replacement real. Broadcast the LOGOUT and the LOGIN_SUCCESS
        // back to back so sibling tabs reset the previous provider's profile/queue/charts
        // and adopt the new token as one unit — dispatch order is preserved on the wire, so
        // they can never end up holding the new token on top of the old identity.
        dispatch(logout())
        dispatch({ type: LOGIN_SUCCESS, payload: { AccessToken: result.data.AccessToken } })
        dispatch(getProviderProfile())

        navigate(location ? `/${location}` : getDefaultHomePath(decodeRolesFromToken(result.data.AccessToken)), {
          replace: true,
        })
      } catch {
        navigate(WAITING_ROOM_PAGE_PATH, { replace: true })
      } finally {
        clearTimeout(timeoutId)
      }
    }

    authenticate()

    return () => controller.abort()
  }, [dispatch, navigate])

  return (
    <div data-testid='pw_auth-validate-loading'>
      <PageLoadingSpinner />
    </div>
  )
}
